import { AntDesign } from '@expo/vector-icons';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { Modal, Portal } from 'react-native-paper';
import DateTimePicker from 'react-native-ui-datepicker';
import { DatePickerBaseProps } from 'react-native-ui-datepicker/lib/typescript/types';

import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import CloseButton from '../buttons/CloseButton';
import Text from '../Text';

export type DatePickerInputFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control?: Control<T>;
  variant?: 'default' | 'rounded_with_shadow';
  label?: string;
  infoSubLabel?: string;
  timePicker?: boolean;
  minDate?: DatePickerBaseProps['minDate'];
  maxDate?: DatePickerBaseProps['maxDate'];
};

const DatePickerInputField = <T extends FieldValues>({
  name,
  control,
  variant = 'default',
  label,
  infoSubLabel,
  timePicker = true,
  minDate,
  maxDate
}: DatePickerInputFieldProps<T>) => {
  const colors = useColor();

  const [showPicker, setShowPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <Portal>
            <Modal
              style={{ alignItems: 'center' }}
              visible={showPicker}
              onDismiss={() => setShowPicker(false)}
              contentContainerStyle={style.modal}>
              <View style={{ flexDirection: 'column' }}>
                <CloseButton
                  style={style.closeButton}
                  onCloseTap={() => {
                    setShowPicker(false);
                    field.value && setSelectedDate(field.value);
                  }}
                />

                <Text fontWeight="900" style={style.selectDateHeader}>
                  {i18n.t('mix.select_date')}
                </Text>

                <DateTimePicker
                  headerContainerStyle={{ marginTop: 45 }}
                  mode="single"
                  date={selectedDate ?? new Date()}
                  timePicker={timePicker}
                  selectedItemColor="black"
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(params) => {
                    if (params.date && timePicker) {
                      setSelectedDate(parseISO(params.date.toLocaleString()).toISOString());
                    } else if (params.date && !timePicker) {
                      setSelectedDate(new Date(params.date.toLocaleString()).toISOString());
                    } else {
                      field.onChange(null);
                    }
                  }}
                />

                <BrandedButton
                  onPress={() => {
                    field.onChange(selectedDate);
                    setShowPicker(false);
                  }}>
                  {i18n.t('mix.apply')}
                </BrandedButton>
              </View>
            </Modal>
          </Portal>
          <View>
            {label && (
              <Text fontWeight="700" style={[style.label, { marginBottom: infoSubLabel ? 5 : 10 }]}>
                {label}
              </Text>
            )}
            {infoSubLabel && <Text style={style.infoSubLabel}>{infoSubLabel}</Text>}
            <View style={getInputStyle(variant)}>
              <TouchableOpacity onPress={() => setShowPicker(true)}>
                <View style={style.leftIcon}>
                  <AntDesign name="calendar" size={20} color="#808080" />
                </View>

                <TextInput
                  value={
                    field.value
                      ? format(field.value.toLocaleString(), `MM/dd/yyyy ${timePicker ? 'hh:mm a' : ''}`)
                      : i18n.t('mix.select_date')
                  }
                  allowFontScaling={false}
                  pointerEvents="none"
                  placeholderTextColor={colors.placeholderText}
                  style={style.inputContainer}
                />
              </TouchableOpacity>
            </View>

            {!!error && <Text style={style.errorText}>{error.message}</Text>}
          </View>
        </>
      )}
    />
  );
};

const getInputStyle = (variant: string) => {
  switch (variant) {
    case 'rounded_with_shadow':
      return style.roundedWithShadow;
    default:
      return style.default;
  }
};

const style = StyleSheet.create({
  modal: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 20
  },

  closeButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 1
  },

  selectDateHeader: {
    fontSize: 22,
    color: 'black',
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center'
  },

  default: {
    padding: 16,
    borderWidth: 1,
    borderRadius: 8
  },

  roundedWithShadow: {
    height: 38,
    backgroundColor: 'white',
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderRadius: 50,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5
  },

  label: {
    color: 'black'
  },

  infoSubLabel: {
    color: 'gray',
    fontStyle: 'italic',
    fontSize: 14,
    marginBottom: 10
  },

  inputContainer: {
    position: 'relative',
    verticalAlign: 'middle',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 'normal',
    paddingLeft: 25,
    color: '#00000099'
  },

  leftIcon: {
    position: 'absolute',
    left: 0,
    zIndex: 1
  },

  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  }
});

export default DatePickerInputField;
